import { sanitize } from "dompurify";

/**
 * Action types
 * @property {string} USER_UPDATED
 */
export const types = {
  USER_CLEAR: "USER_CLEAR",
  USER_UPDATED: "USER_UPDATED",
  USER_WATCH_CLEAR: "USER_WATCH_CLEAR",
  USER_WATCH_UPDATE: "USER_WATCH_UPDATE",
};

const initState = {
  email: null,
  first_name: null,
  last_name: null,
  name: null,
  phone: null,
};

/**
 * @namespace Reducers
 */
export const reducers = {
  /**
   * Updates user state
   * * Member of login redux-persist reducers so that these
   *     values stick around longer than a single session
   * * Access via state.login.user
   * @param {object} state
   * @param {object} action
   * @param {string} action.type
   * @param {object} action.user
   * @param {string} action.user.email
   * @param {string} action.user.first_name
   * @param {string} action.user.last_name
   * @param {string} action.user.name
   * @param {string} action.user.phone
   * @return {state}
   * @memberof Reducers
   */
  user: (state = initState, action) => {
    switch (action.type) {
      case types.USER_UPDATED:
        return { ...state, ...action.user };
      default:
        return state;
    }
  },

  /**
   * Updates watched listings state
   * * Member of login redux-persist reducers so that these
   *     values stick around longer than a single session
   * * Access via state.login.watchedListings
   */
  userWatchedListings: (state = [], action) => {
    switch (action.type) {
      case types.USER_WATCH_UPDATE:
        return action.watchedListings;
      default:
        return state;
    }
  },
};

/**
 * @namespace Actions
 */
export const actions = {
  /**
   * Clear user
   * @memberof Actions
   */
  clear: () => dispatch => {
    dispatch(actions.handleUpdate(initState));
  },

  /**
   * Handle user updated event
   * @param {object} user
   * @param {string} user.email
   * @param {string} user.first_name
   * @param {string} user.last_name
   * @param {string} user.name
   * @param {string} user.phone
   * @memberof Actions
   */
  handleUpdate: user => ({
    type: types.USER_UPDATED,
    user: user,
  }),

  /**
   * Update user
   * @param {object} user
   * @param {string} user.email
   * @param {string} user.first_name
   * @param {string} user.last_name
   * @param {string} user.name
   * @param {string} user.phone
   * @memberof Actions
   */
  update: user => (dispatch, getState) => {
    const update = {};
    const { login } = getState();
    const prevUser = login.user;

    Object.keys(user).forEach(key => {
      // Don't redefine the user's email if it's already
      // been defined from the authentication object
      // since users can't change this value
      if (!!prevUser.email && key === "email") {
        return;
      }

      const clean = sanitize(user[key]);

      if (clean !== prevUser[key]) {
        update[key] = clean;
      }
    });

    if (!!update.first_name || !!update.last_name) {
      const first_name = update.first_name || prevUser.first_name;
      const last_name = update.last_name || prevUser.last_name;
      update.name = `${first_name} ${last_name}`;
    }

    if (!!Object.keys(update).length) {
      dispatch(actions.handleUpdate(update));
    }
  },

  /**
   * Add watched listing
   * @param {number|string} listingId
   * @memberof Actions
   */
  addWatchedListing: listingId => (dispatch, getState) => {
    const {
      login: { userWatchedListings },
    } = getState();

    listingId = parseInt(listingId, 10);

    if (
      !isNaN(listingId) &&
      !userWatchedListings.some(id => id === listingId)
    ) {
      const update = [...userWatchedListings, listingId].sort((a, b) => a - b);
      dispatch(actions.updateWatchedListings(update));
    }
  },

  /**
   * Clear all watched listings
   * @memberof Actions
   */
  clearWatchedListings: () => dispatch => {
    dispatch(actions.updateWatchedListings([]));
  },

  /**
   * Remove watched listing
   * @param {number|string} listingId
   * @memberof Actions
   */
  removeWatchedListing: listingId => (dispatch, getState) => {
    const {
      login: { userWatchedListings },
    } = getState();

    listingId = parseInt(listingId, 10);

    if (!isNaN(listingId)) {
      const update = userWatchedListings.filter(id => id !== listingId);
      dispatch(actions.updateWatchedListings(update));
    }
  },

  /**
   * Update watched listings updated event
   * @param {array} watchedListings
   * @memberof Actions
   */
  updateWatchedListings: watchedListings => ({
    type: types.USER_WATCH_UPDATE,
    watchedListings: watchedListings,
  }),
};
